import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Users } from "lucide-react";
import { S, SERIF, SANS, IMG, Eyebrow, Reveal, H2, Grain, Photo } from "./slUi";

const GROUPS = [["A partner", 2], ["A family", 5], ["Friends", 9], ["A team", 30], ["A community", 240]];

const Dots = ({ n }) => (
  <div className="flex flex-wrap gap-1.5 content-start min-h-[120px]" data-testid="sl-together-dots">
    {Array.from({ length: Math.min(n, 240) }).map((_, i) => (
      <motion.span key={i} initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: Math.min(i * 0.004, 0.9), duration: 0.4 }}
        className="rounded-full" style={{ width: n > 50 ? 7 : 12, height: n > 50 ? 7 : 12, background: i === 0 ? S.gold : "rgba(245,236,217,0.55)" }} />
    ))}
  </div>
);

export const Together = () => {
  const [g, setG] = useState(2);
  const [day, setDay] = useState(1);
  useEffect(() => { const t = setInterval(() => setDay((d) => (d % 21) + 1), 900); return () => clearInterval(t); }, []);
  const n = GROUPS[g][1];
  return (
    <section className="relative overflow-hidden px-6 sm:px-10 lg:px-20 py-28 sm:py-36" style={{ background: S.night, color: S.cream }} id="sl-together" data-testid="sl-together">
      <img src={IMG.together} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(13,24,48,0.35), rgba(13,24,48,0.82) 55%, rgba(13,24,48,0.95))" }} />
      <Grain />
      <div className="relative max-w-5xl mx-auto">
        <Reveal><Eyebrow light>21 Days Together</Eyebrow></Reveal>
        <Reveal delay={0.05}><H2 className="mt-4">An installation doesn't have to be something <span className="" style={{ color: S.gold }}>you do alone.</span></H2></Reveal>
        <Reveal delay={0.1}><p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>Invite the people you care about to follow the same 21-day rhythm with you. Everyone listens in their own time, through their own planner, returning to the same intention each day. It's optional and simple. No leaderboards, no pressure.</p></Reveal>
        <div className="mt-12 grid lg:grid-cols-[1fr,1.1fr] gap-8 items-start">
          <div>
            <div className="flex flex-wrap gap-2">
              {GROUPS.map(([l], i) => (
                <button key={l} type="button" onClick={() => setG(i)} data-testid={`sl-together-group-${i}`} className="rounded-full px-4 py-2 text-sm transition-colors duration-300"
                  style={{ fontFamily: SANS, background: g === i ? S.gold : "transparent", color: g === i ? S.night : S.cream, border: `1px solid ${g === i ? S.gold : S.goldSoft}` }}>{l}</button>
              ))}
            </div>
            <div className="mt-6 rounded-2xl p-5" style={{ background: "rgba(13,24,48,0.7)", border: `1px solid ${S.goldSoft}`, backdropFilter: "blur(14px)" }}>
              <Dots key={n} n={n} />
              <p className="mt-4 inline-flex items-center gap-2 text-sm" style={{ fontFamily: SANS }} data-testid="sl-together-signal"><Users size={14} color={S.gold} /> Day {day} of 21 · {n > 2 ? `You're sharing this intention with ${n - 1} others` : "You and one other, together"}</p>
              <p className="mt-1 text-xs" style={{ fontFamily: SANS, color: "#9fb39a" }}>Another day together ✓</p>
            </div>
          </div>
          <div>
            <p className="text-4xl sm:text-5xl font-light leading-tight" style={{ fontFamily: SERIF }}>One intention.<br />21 days.<br /><span className="" style={{ color: S.gold }}>Together.</span></p>
            <div className="mt-10 rounded-2xl overflow-hidden" style={{ background: S.ivory, color: S.ink }} data-testid="sl-gift-card">
              <Photo src={IMG.gift} alt="A warm embrace" ratio="aspect-[16/9]" className="!rounded-none" pos="center 35%" />
              <div className="p-6">
              <p className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.3em]" style={{ fontFamily: SANS, color: S.bronze }}><Gift size={13} /> Gift</p>
              <p className="mt-3 text-2xl" style={{ fontFamily: SERIF }}>Sometimes you know someone who could use a little more peace, confidence or belief.</p>
              <p className="mt-2 text-sm leading-relaxed" style={{ fontFamily: SANS, color: S.inkDim }}>Create or choose something meaningful and send it as a Soulumo experience. Then Soulumo gently asks:</p>
              <p className="mt-4 text-xl " style={{ fontFamily: SERIF, color: S.bronze }}>"Would you like to do this together for 21 days?"</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
